import React from 'react';

/** Matches the former export popup size (~A4 at 96dpi) and the export tab viewport. */
export const EXPORT_VIEWPORT_PX = { width: 794, height: 1122 } as const;

export function openExportWindow(url: string): Window | null {
  const win = window.open(url, '_blank');
  if (!win) return null;

  try {
    win.opener = null;
    win.focus();
  } catch {
    // Ignore browser restrictions
  }

  return win;
}

type IsPrintingContextValue = {
  isPrinting: boolean;
  setIsPrinting: React.Dispatch<React.SetStateAction<boolean>>;
};

const IsPrintingContext = React.createContext<IsPrintingContextValue | undefined>(undefined);

type IsPrintingProviderProps = React.PropsWithChildren<{
  initialValue?: boolean;
}>;

export function IsPrintingProvider({ children, initialValue = false }: IsPrintingProviderProps) {
  const [isPrinting, setIsPrinting] = React.useState(initialValue);

  React.useEffect(() => {
    setIsPrinting(initialValue);
  }, [initialValue]);

  const value = React.useMemo(
    () => ({
      isPrinting,
      setIsPrinting,
    }),
    [isPrinting]
  );

  return <IsPrintingContext.Provider value={value}>{children}</IsPrintingContext.Provider>;
}

export function useIsPrinting() {
  const context = React.useContext(IsPrintingContext);

  if (!context) {
    throw new Error('useIsPrinting must be used within IsPrintingProvider');
  }

  return context;
}
